import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  Archive,
  Check,
  Database,
  FileText,
  Landmark,
  Lock,
  Pencil,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Server,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Trash2,
  Upload,
  UserCog,
  Users,
  KeyRound,
} from "lucide-react";
import { CCSideNav, type CCNavGroup } from "../components/cc/SideNav";
import { trpc } from "../hooks/trpc";
import { useAuth } from "../hooks/useAuth";
import { RoleCreateForm } from "../components/admin/RoleCreateForm";
import { ServiceIdentityView } from "../components/admin/ServiceIdentityView";
import { UserManagementView } from "../components/admin/UserManagementView";
import { CorePeopleView } from "../components/admin/CorePeopleView";
import { MonitoringPage } from "./MonitoringPage";
import { AuditPage } from "./AuditPage";
import { ImportPage } from "./ImportPage";
import { BankenPage } from "./BankenPage";
import { BerichtePage } from "./BerichtePage";
import { SecurityPage } from "./SecurityPage";
import { StammdatenPage } from "./StammdatenPage";
import { INSTANCE } from "../lib/instance";
import { getAppLabel, isAppEnabledForInstance } from "../../../shared/app-catalog";
import { canManageContactData, isGfRole } from "../../../shared/permissions";
import { isCoreInstance } from "../../../shared/instance";

// Einziger Administrationsbereich. Die Route selbst schützt alle Ansichten
// ausser /admin/stammdaten als GF-only; dieser Guard bleibt absichtlich als
// zweite UX-Schranke bestehen und erweitert keine Serverberechtigung.
const ICON_SIZE = 15;
const STROKE = 1.6;

const VIEWS = [
  "users", "roles", "devices", "dienste", "apps", "monitoring",
  "audit", "import", "banken", "berichte", "security", "stammdaten",
] as const;
type ViewId = (typeof VIEWS)[number];

/** Frühere Slugs, die auf eine zusammengelegte Ansicht zeigen. */
const VIEW_ALIASES: Record<string, ViewId> = {
  logs: "audit",
  // „Nutzer und Personen“ ist seit 04.11.2026 die Spalte Person der Benutzertabelle.
  personen: "users",
};

function resolveView(slug: string | undefined): ViewId {
  if (!slug) return "users";
  if (slug in VIEW_ALIASES) return VIEW_ALIASES[slug];
  return (VIEWS as readonly string[]).includes(slug) ? (slug as ViewId) : "users";
}

/** Ansichten, die eine vollständige Seite mit eigener Überschrift einbetten. */
const EMBEDDED_PAGES: readonly ViewId[] = [
  "stammdaten", "monitoring", "audit", "import", "banken", "berichte", "security",
];

const TITLE_MAP: Partial<Record<ViewId, { eyebrow: string; title: string; sub: string }>> = {
  users: {
    eyebrow: "Administration · Benutzer",
    title: "Benutzer",
    sub: "Konten anlegen, Rollen zuordnen, mit einer Person verknüpfen (Quelle des personUuid-Claims), Passwörter zurücksetzen, sperren oder reaktivieren und DSGVO-Auskunft erstellen.",
  },
  roles: {
    eyebrow: "Administration · Rollen",
    title: "Rollen und App-Zugriff",
    sub: "Individuelle Rollen verwalten und ihren Zugriff auf Anwendungen festlegen. Systemrollen bleiben geschützt.",
  },
  dienste: {
    eyebrow: "Administration · Dienste",
    title: "Dienstidentitäten",
    sub: "Technische Identitäten ohne Benutzerkonto. Tokens nur ausstellen oder widerrufen, wenn der Dienstvertrag dies vorsieht.",
  },
  devices: {
    eyebrow: "Administration · Gemerkte Geräte",
    title: "Gemerkte Geräte",
    sub: "Geräte, auf denen der 2FA-Schritt beim Login für die zulässige Frist übersprungen wird. Abgelaufene und widerrufene Geräte bleiben als Historie erhalten.",
  },
  apps: {
    eyebrow: "Administration · Anwendungen",
    title: "Anwendungsregister",
    sub: "Registrierte Anwendungen und ihr Healthcheck-Status.",
  },
};

/**
 * Core nutzt das vorhandene SATELLITES-Produktprofil (Money, Scan, Life,
 * Story). Es wird keine neue Umgebungsvariable eingeführt. Fachfremde
 * Firmen-Datenansichten bleiben als Altpfad erhalten, erscheinen jedoch nicht
 * im operativen Menü dieser Instanz.
 */
function usesCoreOperationalProfile(): boolean {
  // Menue-/Produktidentitaet ist keine Berechtigung. Eine spaeter hinzugefuegte
  // private App soll Core nicht versehentlich zu einem Firmenmenue machen.
  return isCoreInstance(INSTANCE);
}

/** Summe offener Bankwarnungen für das Badge an "Banken". */
function useBankAlertCount(enabled: boolean): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const load = async () => {
      try {
        const response = await fetch("/api/banks/status", { credentials: "include", cache: "no-store" });
        if (!response.ok) return;
        const payload = await response.json() as {
          summary?: { warning?: number; critical?: number; actionRequired?: number; unavailable?: number };
        };
        const summary = payload.summary ?? {};
        const next = (summary.warning ?? 0) + (summary.critical ?? 0)
          + (summary.actionRequired ?? 0) + (summary.unavailable ?? 0);
        if (active) setCount(Math.max(0, next));
      } catch {
        // Die Navigation bleibt nutzbar; die Detailseite meldet Fehler selbst.
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 60_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [enabled]);
  return count;
}

export function HubAdminPanel({ view }: { view?: ViewId } = {}) {
  const navigate = useNavigate();
  const params = useParams<{ view?: string }>();
  const { user } = useAuth();
  const coreProfile = usesCoreOperationalProfile();
  // /admin/stammdaten is an exact, separately guarded route and therefore
  // has no :view param. Its explicit view must not fall back to users.
  const requestedView = resolveView(view ?? params.view);
  // Only company-role/personnel/data views become the private people view.
  // Existing monitoring, device, audit and security controls stay available.
  const companyOnlyViews: readonly ViewId[] = ["roles", "stammdaten", "import", "banken"];
  const active = coreProfile && companyOnlyViews.includes(requestedView) ? "users" : requestedView;
  const isGf = coreProfile ? user?.coreAdministrator === true : isGfRole(user?.roleName, INSTANCE, user?.roleScope);
  const isContactDataRole = canManageContactData(user?.roleName, INSTANCE, user?.roleScope);

  // Nur die explizite Stammdatenansicht darf zusätzlich für Verwaltung gerendert
  // werden. Direkte URLs zu allen anderen Adminansichten bleiben GF-only.
  const isAllowed = isGf || (active === "stammdaten" && isContactDataRole);
  const bankAlertCount = useBankAlertCount(isGf && !coreProfile);

  const groups: CCNavGroup[] = useMemo(() => {
    if (coreProfile) {
      return [
        {
          label: "Personen",
          items: [
            { id: "users", label: "Personen und Apphäkchen", icon: <Users size={ICON_SIZE} strokeWidth={STROKE} /> },
            { id: "devices", label: "Gemerkte Geräte", icon: <Smartphone size={ICON_SIZE} strokeWidth={STROKE} /> },
            { id: "dienste", label: "Dienste", icon: <KeyRound size={ICON_SIZE} strokeWidth={STROKE} /> },
          ],
        },
        {
          label: "Betrieb",
          items: [
            { id: "apps", label: "Anwendungsregister", icon: <Server size={ICON_SIZE} strokeWidth={STROKE} /> },
            { id: "monitoring", label: "Monitoring", icon: <Activity size={ICON_SIZE} strokeWidth={STROKE} /> },
            { id: "audit", label: "Audit", icon: <ScrollText size={ICON_SIZE} strokeWidth={STROKE} /> },
            { id: "security", label: "Sicherheit", icon: <Lock size={ICON_SIZE} strokeWidth={STROKE} /> },
          ],
        },
        { label: "Daten", items: [{ id: "berichte", label: "Berichte", icon: <FileText size={ICON_SIZE} strokeWidth={STROKE} /> }] },
      ];
    }
    if (!isGf) {
      return [{
        label: "Daten",
        items: [{ id: "stammdaten", label: "Stammdaten", icon: <Database size={ICON_SIZE} strokeWidth={STROKE} /> }],
      }];
    }

    const dataItems = [
      ...(!coreProfile ? [{ id: "stammdaten", label: "Stammdaten", icon: <Database size={ICON_SIZE} strokeWidth={STROKE} /> }] : []),
      ...(!coreProfile ? [{ id: "import", label: "Import", icon: <Upload size={ICON_SIZE} strokeWidth={STROKE} /> }] : []),
      ...(!coreProfile ? [{
        id: "banken",
        label: "Banken",
        icon: <Landmark size={ICON_SIZE} strokeWidth={STROKE} />,
        ...(bankAlertCount > 0 ? { badge: bankAlertCount > 99 ? "99+" : bankAlertCount } : {}),
      }] : []),
      { id: "berichte", label: "Berichte", icon: <FileText size={ICON_SIZE} strokeWidth={STROKE} /> },
    ];

    return [
      {
        label: "Benutzer und Rechte",
        items: [
          { id: "users", label: "Benutzer", icon: <Users size={ICON_SIZE} strokeWidth={STROKE} /> },
          { id: "roles", label: "Rollen und App-Zugriff", icon: <UserCog size={ICON_SIZE} strokeWidth={STROKE} /> },
          { id: "devices", label: "Gemerkte Geräte", icon: <Smartphone size={ICON_SIZE} strokeWidth={STROKE} /> },
          { id: "dienste", label: "Dienste", icon: <KeyRound size={ICON_SIZE} strokeWidth={STROKE} /> },
        ],
      },
      {
        label: "Betrieb",
        items: [
          { id: "apps", label: "Anwendungsregister", icon: <Server size={ICON_SIZE} strokeWidth={STROKE} /> },
          { id: "monitoring", label: "Monitoring", icon: <Activity size={ICON_SIZE} strokeWidth={STROKE} /> },
          { id: "audit", label: "Audit", icon: <ScrollText size={ICON_SIZE} strokeWidth={STROKE} /> },
          { id: "security", label: "Sicherheit", icon: <Lock size={ICON_SIZE} strokeWidth={STROKE} /> },
        ],
      },
      { label: "Daten", items: dataItems },
    ];
  }, [bankAlertCount, coreProfile, isGf]);

  const onChange = (id: string) => {
    const next = resolveView(id);
    if (next === "users") navigate("/admin");
    else navigate(`/admin/${next}`);
  };

  const titleInfo = coreProfile && active === "users"
    ? { eyebrow: "Administration · Core", title: "Personen", sub: "Jede Person sieht nur ihre eigenen Inhalte. Administratorrechte und App-Zugriff legst du getrennt fest." }
    : EMBEDDED_PAGES.includes(active) ? undefined : TITLE_MAP[active];

  if (!isAllowed) {
    return (
      <div className="cc-root" data-app="admin" data-testid="admin-root">
        <div className="cc-page" data-testid="admin-forbidden">
          <header className="cc-page-title">
            <div className="cc-page-title-main">
              <div className="cc-eyebrow">Administration</div>
              <h1 className="cc-title">Kein Zugriff</h1>
              <p className="cc-sub">Diese Administrationsansicht ist nur für die Geschäftsführung verfügbar.</p>
            </div>
          </header>
          <div className="cc-card"><div className="cc-card-body">
            <span className="cc-badge-st is-bad">403 Forbidden</span>
          </div></div>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-root" data-app="admin" data-testid="admin-root">
      <CCSideNav
        groups={groups}
        value={active}
        onChange={onChange}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="cc-row-label">{user?.username ?? "—"}</span>
            <span className="cc-badge-st is-info">{coreProfile ? "Administrator" : user?.roleName ?? "—"}</span>
          </div>
        }
      />

      <div className="cc-page" data-testid="admin-page">
        {titleInfo ? (
          <header className="cc-page-title"><div className="cc-page-title-main">
            <div className="cc-eyebrow">{titleInfo.eyebrow}</div>
            <h1 className="cc-title">{titleInfo.title}</h1>
            <p className="cc-sub">{titleInfo.sub}</p>
          </div></header>
        ) : null}

        {active === "stammdaten" && <StammdatenPage />}
        {active === "users" && isGf && (coreProfile ? <CorePeopleView /> : <UserManagementView canLinkPersons={isGf} />)}
        {active === "roles" && isGf && <RolesView />}
        {active === "devices" && isGf && <DevicesView />}
        {active === "dienste" && isGf && <ServiceIdentityView />}
        {active === "apps" && isGf && <AppsView />}
        {active === "monitoring" && isGf && <MonitoringPage />}
        {active === "audit" && isGf && <AuditPage initialPreset={params.view === "logs" ? "auth" : "all"} />}
        {active === "security" && isGf && <SecurityPage />}
        {active === "import" && isGf && <ImportPage />}
        {active === "banken" && isGf && <BankenPage />}
        {active === "berichte" && isGf && <BerichtePage />}
      </div>
    </div>
  );
}

// --- Rollen & App-Zugriff (nur GF) --------------------------------------

function RolesView() {
  const utils = trpc.useUtils();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editedLabel, setEditedLabel] = useState("");
  const matrixQuery = trpc.admin.rbacMatrix.useQuery({ includeArchived });

  const refreshRoles = async () => {
    await Promise.all([
      utils.admin.rbacMatrix.invalidate(),
      utils.admin.listRoles.invalidate(),
    ]);
  };
  const setPermission = trpc.admin.rbacSetPermission.useMutation({ onSuccess: refreshRoles });
  const updateLabel = trpc.admin.rbacUpdateRoleLabel.useMutation({
    onSuccess: async () => {
      setEditingRoleId(null);
      setEditedLabel("");
      await refreshRoles();
    },
  });
  const archiveRole = trpc.admin.rbacArchiveRole.useMutation({ onSuccess: refreshRoles });
  const restoreRole = trpc.admin.rbacRestoreRole.useMutation({ onSuccess: refreshRoles });
  const deleteRole = trpc.admin.rbacDeleteRole.useMutation({ onSuccess: refreshRoles });

  const isChangingRole = updateLabel.isPending || archiveRole.isPending
    || restoreRole.isPending || deleteRole.isPending;
  const matrixByRoleAndApp = useMemo(() => new Map(
    (matrixQuery.data?.matrix ?? []).map((entry) => [`${entry.roleId}:${entry.appId}`, entry]),
  ), [matrixQuery.data?.matrix]);

  const startLabelEdit = (role: { id: number; label: string }) => {
    setEditingRoleId(role.id);
    setEditedLabel(role.label);
  };
  const saveLabel = (roleId: number) => {
    const label = editedLabel.trim();
    if (!label) return;
    updateLabel.mutate({ roleId, label });
  };

  return (
    <div className="cc-card" data-testid="admin-roles">
      <div className="cc-card-head">
        <span className="cc-card-title">Rollenverwaltung</span>
        <span className="cc-pill">{isCoreInstance(INSTANCE) ? "Inhaber" : "GF"}</span>
      </div>
      <div className="cc-card-body">
        <RoleCreateForm onCreated={() => { void refreshRoles(); }} />

        <label className="flex items-center gap-2 text-xs mb-4" style={{ color: "var(--text-2)" }}>
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          Archivierte Rollen anzeigen
        </label>

        {matrixQuery.isLoading && <div>Rollenmatrix wird geladen…</div>}
        {matrixQuery.error && <span className="cc-badge-st is-bad">{matrixQuery.error.message}</span>}
        {matrixQuery.data ? (
          <div style={{ overflowX: "auto" }}>
            <table className="table" aria-label="Rolle-App-Matrix" style={{ minWidth: 920, tableLayout: "fixed" }}>
              <thead><tr>
                <th style={{ width: 300 }}>Rolle</th>
                {matrixQuery.data.apps.map((app) => (
                  <th key={app.id} style={{ minWidth: 108, textAlign: "center", whiteSpace: "normal" }}>
                    {getAppLabel(app.key, app.label)}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {matrixQuery.data.roles.map((role) => {
                  const archived = role.archivedAt !== null;
                  const canArchive = !role.isSystem && !archived && role.assignedUsers === 0;
                  const canDelete = !role.isSystem && archived && role.assignedUsers === 0;
                  return (
                    <tr key={role.id} style={archived ? { opacity: 0.62 } : undefined}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {editingRoleId === role.id ? (
                            <>
                              <input
                                className="input"
                                aria-label={`Anzeigename für ${role.key}`}
                                value={editedLabel}
                                maxLength={80}
                                onChange={(event) => setEditedLabel(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") saveLabel(role.id);
                                  if (event.key === "Escape") setEditingRoleId(null);
                                }}
                                style={{ minWidth: 150, maxWidth: 210 }}
                                autoFocus
                              />
                              <button type="button" className="cc-btn" onClick={() => saveLabel(role.id)} disabled={isChangingRole || !editedLabel.trim()}>
                                <Check size={13} strokeWidth={STROKE} /> Speichern
                              </button>
                            </>
                          ) : (
                            <>
                              <strong style={{ overflowWrap: "anywhere" }}>{role.label}</strong>
                              {!role.isSystem && !archived ? (
                                <button type="button" className="cc-btn" onClick={() => startLabelEdit(role)} disabled={isChangingRole} title="Anzeigename bearbeiten">
                                  <Pencil size={13} strokeWidth={STROKE} />
                                </button>
                              ) : null}
                            </>
                          )}
                        </div>
                        <div className="cc-row-label" style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
                          <span style={{ fontFamily: "var(--font-shell-mono)" }}>{role.key}</span>
                          {role.isSystem ? <span className="cc-badge-st is-info" title="Systemrollen sind geschützt und können nicht umbenannt, archiviert oder gelöscht werden.">Systemrolle geschützt</span> : <span>Individuelle Rolle</span>}
                          {role.locked ? <span className="cc-badge-st is-warn">Feste Freigaben</span> : null}
                          {archived ? <span className="cc-badge-st is-bad">Archiviert</span> : null}
                          <span>{role.assignedUsers} zugeordnet</span>
                        </div>
                        {!role.isSystem ? (
                          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                            {archived ? (
                              <button type="button" className="cc-btn" disabled={isChangingRole} onClick={() => restoreRole.mutate({ roleId: role.id })}>
                                <RotateCcw size={13} strokeWidth={STROKE} /> Reaktivieren
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="cc-btn"
                                disabled={!canArchive || isChangingRole}
                                title={canArchive ? "Rolle archivieren" : "Nur nicht zugeordnete individuelle Rollen können archiviert werden."}
                                onClick={() => {
                                  if (confirm(`Die Rolle „${role.label}“ archivieren?`)) archiveRole.mutate({ roleId: role.id });
                                }}
                              >
                                <Archive size={13} strokeWidth={STROKE} /> Archivieren
                              </button>
                            )}
                            {canDelete ? (
                              <button
                                type="button"
                                className="cc-btn"
                                disabled={isChangingRole}
                                onClick={() => {
                                  if (confirm(`Die archivierte Rolle „${role.label}“ endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
                                    deleteRole.mutate({ roleId: role.id });
                                  }
                                }}
                              >
                                <Trash2 size={13} strokeWidth={STROKE} /> Löschen
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </td>
                      {matrixQuery.data.apps.map((app) => {
                        const entry = matrixByRoleAndApp.get(`${role.id}:${app.id}`);
                        const pending = setPermission.isPending
                          && setPermission.variables?.roleId === role.id
                          && setPermission.variables?.appId === app.id;
                        return (
                          <td key={app.id} style={{ textAlign: "center", verticalAlign: "middle" }}>
                            {app.accessMode === "private-users" ? (
                              <span className="cc-badge-st is-info">Individuell</span>
                            ) : entry?.restriction ? (
                              <span
                                className="cc-badge-st is-warn"
                                title={entry.restriction}
                              >
                                {app.key === "money" ? "Nur Inhaber" : "Gesperrt"}
                              </span>
                            ) : (
                              <input
                                type="checkbox"
                                aria-label={`${role.label}: ${getAppLabel(app.key, app.label)}`}
                                checked={entry?.granted ?? false}
                                disabled={archived || pending || entry?.editable === false}
                                onChange={() => setPermission.mutate({
                                  roleId: role.id,
                                  appId: app.id,
                                  granted: !(entry?.granted ?? false),
                                })}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        {setPermission.isError && <p role="alert">Fehler: {setPermission.error.message}</p>}
        {updateLabel.isError && <p role="alert">Fehler: {updateLabel.error.message}</p>}
        {archiveRole.isError && <p role="alert">Fehler: {archiveRole.error.message}</p>}
        {restoreRole.isError && <p role="alert">Fehler: {restoreRole.error.message}</p>}
        {deleteRole.isError && <p role="alert">Fehler: {deleteRole.error.message}</p>}
      </div>
    </div>
  );
}

// --- Gemerkte Geräte (nur GF; eigene Geräte liegen in Einstellungen) ------

function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function DevicesView() {
  const utils = trpc.useUtils();
  const usersQuery = trpc.trustedDevices.adminUsers.useQuery();
  const [selected, setSelected] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const devicesQuery = trpc.trustedDevices.adminListForUser.useQuery(
    { userId: selected ?? 0 },
    { enabled: selected !== null },
  );
  const refresh = () => {
    void utils.trustedDevices.adminUsers.invalidate();
    void utils.trustedDevices.adminListForUser.invalidate();
  };
  const revokeMutation = trpc.trustedDevices.adminRevoke.useMutation({
    onSuccess: refresh,
    onError: (err) => alert(`Fehler: ${err.message}`),
  });
  const revokeAllMutation = trpc.trustedDevices.adminRevokeAll.useMutation({
    onSuccess: refresh,
    onError: (err) => alert(`Fehler: ${err.message}`),
  });

  if (usersQuery.isLoading) return <div className="cc-card"><div className="cc-card-body">Benutzer werden geladen…</div></div>;
  if (usersQuery.error) {
    return <div className="cc-card" data-testid="admin-devices-error"><div className="cc-card-body">
      <span className="cc-badge-st is-bad">Fehler</span><span style={{ marginLeft: 8 }}>{usersQuery.error.message}</span>
    </div></div>;
  }

  const rows = usersQuery.data ?? [];
  const selectedUser = rows.find((item) => item.id === selected) ?? null;
  const allDevices = devicesQuery.data?.devices ?? [];
  const visibleDevices = showHistory ? allDevices : allDevices.filter((device) => device.active);

  return (
    <div style={{ display: "grid", gap: 16 }} data-testid="admin-devices">
      <div className="cc-card"><div className="cc-card-head">
        <span className="cc-card-title">Benutzer</span><span className="cc-pill">{rows.length} Einträge</span>
      </div><div className="cc-card-body is-flush"><div className="cc-list">
        {rows.map((account) => (
          <button
            key={account.id}
            type="button"
            className="cc-list-row"
            onClick={() => { setSelected(account.id); setShowHistory(false); }}
            style={{
              gridTemplateColumns: "1.6fr 110px 90px 130px", textAlign: "left",
              background: selected === account.id ? "var(--surface-2)" : undefined,
              opacity: account.isActive === false ? 0.55 : 1,
            }}
            data-testid={`admin-devices-user-${account.id}`}
          >
            <span>{account.name ?? account.username} <span className="cc-row-label">@{account.username}</span></span>
            <span className="cc-badge-st is-info">{account.roleName}</span>
            <span className={`cc-badge-st ${account.totpEnabled ? "is-ok" : "is-warn"}`}>{account.totpEnabled ? "2FA" : "ohne 2FA"}</span>
            <span className="cc-row-label">{account.activeDevices} aktive Geräte</span>
          </button>
        ))}
      </div></div></div>

      {selectedUser ? (
        <div className="cc-card" data-testid="admin-devices-list"><div className="cc-card-head">
          <span className="cc-card-title">Geräte von {selectedUser.name ?? selectedUser.username}</span>
          <button
            type="button"
            className="cc-btn"
            disabled={revokeAllMutation.isPending}
            onClick={() => {
              if (confirm(`Alle gemerkten Geräte von „${selectedUser.username}“ widerrufen? Laufende Sitzungen werden beendet.`)) {
                revokeAllMutation.mutate({ userId: selectedUser.id });
              }
            }}
          >
            <ShieldOff size={13} strokeWidth={STROKE} /> Alle widerrufen
          </button>
        </div><div className="cc-card-body is-flush">
          <label className="flex items-center gap-2 text-xs" style={{ padding: "12px 14px", color: "var(--text-2)" }}>
            <input type="checkbox" checked={showHistory} onChange={(event) => setShowHistory(event.target.checked)} />
            Gerätehistorie mit abgelaufenen und widerrufenen Geräten anzeigen
          </label>
          <div className="cc-list">
            {visibleDevices.map((device) => (
              <div key={device.id} className="cc-list-row" style={{ gridTemplateColumns: "1.4fr 130px 130px 130px 110px" }}>
                <span>{device.name ?? "Unbekanntes Gerät"}</span>
                <span className="cc-row-label">angelegt {fmtDate(device.createdAt)}</span>
                <span className="cc-row-label">zuletzt {fmtDate(device.lastUsedAt)}</span>
                <span className="cc-row-label">bis {fmtDate(device.expiresAt)}</span>
                {device.active ? (
                  <button
                    type="button"
                    className="cc-btn"
                    disabled={revokeMutation.isPending}
                    onClick={() => {
                      if (confirm("Dieses Gerät widerrufen?")) revokeMutation.mutate({ userId: selectedUser.id, deviceId: device.id });
                    }}
                  >Widerrufen</button>
                ) : <span className="cc-badge-st is-bad">{device.revoked ? "widerrufen" : "abgelaufen"}</span>}
              </div>
            ))}
            {devicesQuery.data && visibleDevices.length === 0 ? (
              <div className="cc-list-row"><span style={{ color: "var(--text-3)" }}>
                {showHistory ? "Keine gemerkten Geräte." : "Keine aktiven gemerkten Geräte. Die Historie kann über den Filter eingeblendet werden."}
              </span></div>
            ) : null}
          </div>
        </div></div>
      ) : null}
    </div>
  );
}

// --- Anwendungen ----------------------------------------------------------

function statusToTone(status: string): "ok" | "warn" | "bad" | "info" {
  if (status === "healthy") return "ok";
  if (status === "unhealthy") return "warn";
  if (status === "unreachable") return "bad";
  return "info";
}

function AppsView() {
  const appsQuery = trpc.admin.listSatelliteApps.useQuery();
  const checkMutation = trpc.admin.checkAppHealth.useMutation({ onSuccess: () => appsQuery.refetch() });

  if (appsQuery.isLoading) return <div className="cc-card"><div className="cc-card-body">Anwendungen werden geladen…</div></div>;
  if (appsQuery.error) {
    return <div className="cc-card" data-testid="admin-apps-error"><div className="cc-card-body">
      <span className="cc-badge-st is-bad">Fehler</span><span style={{ marginLeft: 8 }}>{appsQuery.error.message}</span>
    </div></div>;
  }

  const apps = appsQuery.data?.apps ?? [];
  return (
    <div className="cc-card" data-testid="admin-apps"><div className="cc-card-head">
      <span className="cc-card-title">Anwendungsregister</span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span className="cc-pill">{apps.length} Anwendungen</span>
        <button type="button" className="cc-btn" onClick={() => appsQuery.refetch()} disabled={appsQuery.isFetching} title="Alle erneut laden (nutzt 30s-Cache)">
          <RefreshCw size={13} strokeWidth={STROKE} /> Neu laden
        </button>
      </div>
    </div><div className="cc-card-body is-flush"><div className="cc-list">
      <div className="cc-list-row" style={{ gridTemplateColumns: "1.2fr 1.6fr 110px 110px 130px 130px", background: "var(--surface-2)", fontWeight: 600 }}>
        <span className="cc-row-label">Name / Basispfad</span><span className="cc-row-label">Interne URL</span><span className="cc-row-label">Version</span>
        <span className="cc-row-label">Status</span><span className="cc-row-label">Letzter Check</span><span className="cc-row-label">Aktion</span>
      </div>
      {apps.map((app) => {
        const tone = statusToTone(app.status);
        const pending = checkMutation.isPending && checkMutation.variables?.appClaim === app.appClaim;
        return (
          <div key={app.appClaim} className="cc-list-row" style={{ gridTemplateColumns: "1.2fr 1.6fr 110px 110px 130px 130px" }} data-testid={`admin-app-${app.appClaim}`}>
            <div><div style={{ fontWeight: 500 }}>{getAppLabel(app.appClaim, app.name)}</div><div className="cc-row-label" style={{ fontSize: 10, marginTop: 2 }}>{app.basePath}</div></div>
            <span className="cc-row-mono" style={{ color: "var(--text-2)", fontSize: 11.5, wordBreak: "break-all" }}>{app.url ?? <em style={{ color: "var(--text-3)" }}>{app.envVar} nicht gesetzt</em>}</span>
            <span className="cc-row-label">—</span>
            <span className={`cc-badge-st is-${tone}`} data-testid={`admin-app-status-${app.appClaim}`}>{app.status}</span>
            <span className="cc-row-mono cc-row-label" style={{ fontSize: 10.5 }}>{app.checkedAt ? `${new Date(app.checkedAt).toLocaleTimeString("de-DE")}${app.latencyMs != null ? ` · ${app.latencyMs}ms` : ""}` : "—"}</span>
            <button type="button" className="cc-btn" onClick={() => checkMutation.mutate({ appClaim: app.appClaim })} disabled={pending} data-testid={`admin-app-check-${app.appClaim}`}>
              <RefreshCw size={13} strokeWidth={STROKE} /> {pending ? "Prüfe…" : "Healthcheck"}
            </button>
          </div>
        );
      })}
      {apps.length === 0 ? <div className="cc-list-row"><span style={{ color: "var(--text-3)" }}>Keine Anwendungen registriert.</span></div> : null}
    </div></div></div>
  );
}
